import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function AuthPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showReset, setShowReset] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: 8 }}>📅</div>
        <h1>{isLogin ? 'Connexion' : 'Créer un compte'}</h1>
        <p className="subtitle">
          {isLogin ? 'Retrouvez votre organisation' : 'Toute votre vie en une seule app'}
        </p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <label>Prénom</label>
              <input className="form-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Votre prénom" required />
            </div>
          )}
          <div className="form-group">
            <label>Email</label>
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vous@email.com" required />
          </div>
          <div className="form-group">
            <label>Mot de passe</label>
            <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="6 caractères minimum" required minLength={6} />
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8, padding: '12px 20px' }} disabled={submitting}>
            {submitting ? 'Chargement...' : (isLogin ? 'Se connecter' : "S'inscrire")}
          </button>
        </form>

        {isLogin && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={() => setShowReset(true)}
              style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
            >
              🔐 Mot de passe oublié ?
            </button>
          </div>
        )}

        <div className="auth-toggle">
          {isLogin ? "Pas encore de compte ? " : "Déjà un compte ? "}
          <button onClick={() => { setIsLogin(!isLogin); setError(''); }}>
            {isLogin ? "S'inscrire" : "Se connecter"}
          </button>
        </div>
      </div>

      {showReset && <ResetPasswordModal onClose={() => setShowReset(false)} initialEmail={email} />}
    </div>
  );
}

function ResetPasswordModal({ onClose, initialEmail }) {
  const [step, setStep] = useState(1); // 1=request email, 2=verify code + new password
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setSubmitting(true);
    try {
      await api.post('/auth/request-reset', { email });
      setInfo('Un fichier a été créé sur votre Bureau : OrgaLife-code-reset.txt');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      setError('Mot de passe trop court (6 caractères min)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { email, code, new_password: newPassword });
      setInfo('✓ Mot de passe réinitialisé ! Vous pouvez vous connecter avec le nouveau.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ margin: 0 }}>🔐 Mot de passe oublié</h2>
          <button type="button" className="btn-icon" onClick={onClose}>✕</button>
        </div>

        {/* ÉTAPE 1 : demander email */}
        {step === 1 && (
          <form onSubmit={requestReset}>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-light)', marginBottom: 14, lineHeight: 1.5 }}>
              Saisissez votre email. Un code sera créé dans un fichier sur votre Bureau.
              Vous pourrez ensuite réinitialiser votre mot de passe.
            </p>
            {error && <div className="error" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 10, borderRadius: 8, fontSize: '0.85rem', marginBottom: 10 }}>{error}</div>}

            <div className="form-group">
              <label>Email</label>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Génération...' : 'Générer le code'}
              </button>
            </div>
          </form>
        )}

        {/* ÉTAPE 2 : entrer le code + nouveau mot de passe */}
        {step === 2 && (
          <form onSubmit={submitReset}>
            {info && (
              <div style={{ background: 'var(--primary-bg)', padding: 12, borderRadius: 10, fontSize: '0.85rem', marginBottom: 14, border: '1px solid var(--primary-light)' }}>
                📂 <strong>{info}</strong>
                <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--text-light)' }}>
                  1. Ouvrez votre Bureau<br />
                  2. Double-cliquez sur <code style={{ background: 'white', padding: '2px 4px', borderRadius: 3 }}>OrgaLife-code-reset.txt</code><br />
                  3. Copiez le code à 6 chiffres ci-dessous
                </div>
              </div>
            )}
            {error && <div className="error" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 10, borderRadius: 8, fontSize: '0.85rem', marginBottom: 10 }}>{error}</div>}

            <div className="form-group">
              <label>Code à 6 chiffres</label>
              <input
                className="form-input"
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                autoFocus
                style={{ letterSpacing: 8, textAlign: 'center', fontSize: '1.2rem', fontWeight: 700 }}
                maxLength={6}
              />
            </div>

            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="6 caractères minimum" required minLength={6} />
            </div>

            <div className="form-group">
              <label>Confirmer le mot de passe</label>
              <input className="form-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>← Retour</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Réinitialisation...' : 'Réinitialiser'}
              </button>
            </div>
          </form>
        )}

        {/* ÉTAPE 3 : succès */}
        {step === 3 && (
          <div>
            <div style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: 16, borderRadius: 10, textAlign: 'center', fontSize: '0.95rem', fontWeight: 600, marginBottom: 16 }}>
              ✓ Mot de passe réinitialisé !
            </div>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-light)', marginBottom: 16 }}>
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe. Le fichier <code>OrgaLife-code-reset.txt</code> a été supprimé de votre Bureau.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={onClose} style={{ width: '100%', justifyContent: 'center' }}>
                Retour à la connexion
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
